<template>
<div class="chapter">
<div class="inner_page">
<h2 class="calibre19" id="pgepubid00250"><a class="calibre1" id="pagexv"></a><span class="calibre1" id="pgepubid00251" title="p. xv"></span>INTRODUCTION</h2>
<p class="calibre7"><span class="smcap">One</span> day in the autumn of 1888, in
the island of Tahiti, during an illness which he supposed might
be his last, Stevenson put into the hands of his stepson, Mr.
Lloyd Osbourne, a sealed paper with the request that it should be
opened after his death.  He recovered, as every one knows,
and had strength enough to enjoy six years more of active life
and work in the Pacific Islands.  When the end came, and the
paper was opened, it was found to contain, among other things,
the expression of his wish that I should be asked to prepare for
publication ‘a selection of his letters and a sketch of his
life.’  The journal letters written to myself from his
Samoan home, subsequently to the date of the request, offered the
readiest material towards fulfilling promptly a part at least of
the duty thus laid upon me; and a selection from these was
accordingly published in the autumn following his death. <a id="citationxv"></a><span class="citation" href="#/Text/Section0011#footnotexv" title="Link target outside of Sample">[xv]</span></p>
<p class="calibre7">The scanty leisure of an official life (chiefly employed as it
was for several years in seeing my friend’s collected and
posthumous works through the press) did not allow me to complete
the remainder of my task without considerable delay.  For
one thing, the body of correspondence <a id="pagexvi"></a><span id="pgepubid00252" title="p. xvi"></span>which came in from various quarters
turned out much larger than had been anticipated, and the labour
of sifting and arranging it much greater.  The author of
<i class="calibre12">Treasure Island</i> and <i class="calibre12">Across the Plains</i> and <i class="calibre12">Weir
of Hermiston</i> did not love writing letters, and will be found
somewhere in the following pages referring to himself as one
‘essentially and originally incapable of the art
epistolary.’  That he was a bad correspondent had even
come to be an accepted view among his friends; but in truth it
was only during one particular period of his life (see below,
vol. i. p. 103) that he at all deserved such a reproach.  At
other times, as is now apparent, he had shown a degree of
industry and spirit in letter-writing extraordinary considering
his health and occupations, and especially considering his
declared aversion for the task.  His letters, it is true,
were often the most informal in the world, and he generally
neglected to date them, a habit which is the despair of editors;
but after his own whim and fashion he wrote a vast number; so
that for every one here included some half-a-dozen at least have
had to be rejected.</p>
<p class="calibre7">In considering the scale and plan on which my friend’s
instruction should be carried out, it seemed necessary to take
into account, not his own always modest opinion of himself, but
the place which, as time went on, he seemed likely to take
ultimately in the world’s regard.  The four or five
years following the death of a writer much applauded in his
lifetime are generally the years when the decline of his
reputation begins, if it is going to suffer decline at all. 
At present, certainly, Stevenson’s name <a id="pagexvii"></a><span id="pgepubid00253" title="p. xvii"></span>seems in
no danger of going down.  On the stream of daily literary
reference and allusion it floats more actively than ever. 
In another sense its vitality is confirmed by the material test
of continued sales and of the market.  Since we have lost
him other writers, whose beginnings he watched with sympathetic
interest, have come to fill a greater immediate place in public
attention; one especially has struck notes which appeal to
dominant fibres in our Anglo-Saxon stock with irresistible force;
but none has exercised Stevenson’s peculiar and personal
power to charm, to attach, and to inspirit.  By his study of
perfection in form and style—qualities for which his
countrymen in general have been apt to care little—he might
seem destined to give pleasure chiefly to the fastidious and the
artistically minded.  But as to its matter, the main appeal
of his work is not to any mental tastes and fashions of the few;
it is rather to universal, hereditary instincts, to the primitive
sources of imaginative excitement and entertainment in the
race.</p>
<p class="calibre7">By virtue, then, of this double appeal of form and matter; by
his especial hold upon the young, in whose spirit so much of his
best work was done; by his undecaying influence on other writers;
by the spell which he still exercises from the grave, and
exercises most strongly on those who are most familiar with the
best company whether of the living or the dead, Stevenson’s
name and memory, so far as can be judged at present, seem
destined not to dwindle, but to grow.  The voice of the
<i class="calibre12">advocatus diaboli</i> has been heard against him, as it is
right and proper that it should be heard <a id="pagexviii"></a><span id="pgepubid00254" title="p. xviii"></span>against
any man before his reputation can be held fully
established.  One such advocate in this country has thought
to dispose of him by the charge of
‘externality.’  But the reader who remembers
things like the sea-frenzy of Gordon Darnaway, or the dialogue of
Markheim with his other self in the house of murder, or the
re-baptism of the spirit of Seraphina in the forest dews, or the
failure of Herrick to find in the waters of the island lagoon a
last release from dishonour, or the death of Goguelat, or the
appeal of Kirstie Elliot in the midnight chamber—such a
reader can only smile at a criticism like this and put it
by.  These and a score of other passages breathe the
essential poetry and significance of things as they reveal
themselves to true masters only—are instinct at once with
the morality and the romance which lie deep together at the soul
of nature and experience.  Not in vain had Stevenson read
the lesson of the Lantern-Bearers, and hearkened to the music of
the pipes of Pan.  He was feeling his way all his life
towards a fuller mastery of his means, preferring always to leave
unexpressed what he felt that he could not express perfectly; and
in much of his work was content merely to amuse himself and
others.  But even when he is playing most fancifully with
his art and his readers, as in the shudders, tempered with
laughter, of the Suicide Club, or the airy sentimental comedy of
Providence and the Guitar, or the schoolboy historical inventions
of Dickon Crookback and the old sailor Arblaster, a writer of his
quality cannot help striking notes from the heart of life and the
inwardness <a id="pagexix"></a><span id="pgepubid00255" title="p. xix"></span>of things deeper than will ever be struck, or even
apprehended, by another who labours, with never a smile either of
his own or of his reader’s, upon the most solemn
enterprises of realistic fiction, but is born without the
magician’s touch and insight.</p>
<p class="calibre7">Another advocate on the same side, in the United States, has
made much of the supposed dependence of this author on his
models, and classed him among writers whose inspiration is
imitative and second-hand.  But this, surely, is to be quite
misled by the well-known passage of Stevenson’s own, in
which he speaks of himself as having in his prentice years played
the ‘sedulous ape’ to many writers of different
styles and periods.  In doing this he was not seeking
inspiration, but simply practising the use of the tools which
were to help him to express his own inspirations.  Truly he
was always much of a reader; but it was life, not books, that
always in the first degree allured and taught him.</p>
<blockquote class="calibre21"><div class="container"><p class="calibre7">‘He loved of life the myriad sides,<br class="calibre5"/>
Pain, prayer, or pleasure, act or sleep,<br class="calibre5"/>
As wallowing narwhals love the deep’—</p>
</div>
</blockquote>
<p class="calibre7">so with just self-knowledge he wrote of himself; and the books
which he most cared for and lived with were those of which the
writers seemed—to quote again a phrase of his own—to
have been ‘eavesdropping at the door of his heart’;
those which told of moods, impressions, experiences or cravings
after experience, pains, pleasures, opinions or conflicts of the
spirit, which in the eagerness of youthful living and thinking
had already <a id="pagexx"></a><span id="pgepubid00256" title="p. xx"></span>been his own.  No man, in fact, was ever less
inclined to take anything at second-hand.  The root of all
originality was in him, in the shape of an extreme natural
vividness of perception, imagination, and feeling.  An
instinctive and inbred unwillingness to accept the accepted and
conform to the conventional was of the essence of his character,
whether in life or art, and was a source to him both of strength
and weakness.  He would not follow a general
rule—least of all if it was a prudential rule—of
conduct unless he was clear that it was right according to his
private conscience; nor would he join, in youth, in the ordinary
social amusements of his class when he had once found out that
they did not amuse <i class="calibre12">him</i>; nor wear their clothes if he could
not feel at ease and be himself in them; nor use, whether in
speech or writing, any trite or inanimate form of words that did
not faithfully and livingly express his thought.  A readier
acceptance of current usages might have been better for him, but
was simply not in his nature.  ‘Damp gingerbread
puppets’ were to him the persons who lived and thought and
felt and acted only as was expected of them.  ‘To see
people skipping all round us with their eyes sealed up with
indifference, knowing nothing of the earth or man or woman, going
automatically to offices and saying they are happy or unhappy,
out of a sense of duty I suppose, surely at least from no sense
of happiness or unhappiness, unless perhaps they have a tooth
that twinges—is it not like a bad dream?’  No
reader of this book will close it, I am sure, without feeling
that he has been throughout in the company of a spirit various <a id="pagexxi"></a><span id="pgepubid00257" title="p. xxi"></span>indeed and
many-mooded, but profoundly sincere and real.  Ways that in
another might easily have been mere signs of affectation were in
him the true expression of a nature ten times more spontaneously
itself and individually alive than that of others. 
Self-consciousness, in many characters that possess it, deflects
and falsifies conduct; and so does the dramatic instinct. 
Stevenson was self-conscious in a high degree, but only as a part
of his general activity of mind; only in so far as he could not
help being an extremely intelligent spectator of his own doings
and feelings; these themselves came from springs of character and
impulse much too deep and strong to be diverted.  He loved
also, with a child’s or actor’s gusto, to play a part
and make a drama out of life; <a id="citationxxi"></a><span class="citation" href="#/Text/Section0011#footnotexxi" title="Link target outside of Sample">[xxi]</span> but the part was
always for the moment his very own: he had it not in him to pose
for anything but what he truly was.</p>
<p class="calibre7">When a man so constituted had once mastered his craft of
letters, he might take up whatever instrument he pleased with the
instinctive and just confidence that he would play upon it to a
tune and with a manner of his own.  This is indeed the true
mark and test of his originality.  He has no need to be, or
to seem, especially original in the form and mode of literature
which he attempts.  By his choice of these he may at any
time give himself and his reader the pleasure of recalling, like
a familiar air, some strain of literary association; but in so
doing he only adds a secondary charm to his work; the vision, the
temperament, the mode of conceiving and handling, are in every
case <a id="pagexxii"></a><span id="pgepubid00258" title="p. xxii"></span>strongly personal to himself.  He may try his
hand in youth at a Sentimental Journey, but R. L. S. cannot
choose but be at the opposite pole of human character and feeling
from Laurence Sterne.  In tales of mystery, allegorical or
other, he may bear in mind the precedent of Edgar Poe, and yet
there is nothing in style and temper much wider apart than
<i class="calibre12">Markheim</i> and <i class="calibre12">Jekyll and Hyde</i> are from the
<i class="calibre12">Murders in the Rue Morgue</i> or <i class="calibre12">William Wilson</i>. 
He may set out to tell a pirate story for boys ‘exactly in
the ancient way,’ and it will come from him not in the
ancient way at all, but re-minted; marked with a sharpness and
saliency in the characters, a private stamp of buccaneering
ferocity combined with smiling humour, an energy of vision and
happy vividness of presentment, which are shiningly his
own.  Another time, he may desert the paths of Kingston and
Ballantyne the brave for those of Sir Walter Scott; but
literature presents few stronger contrasts than between any scene
of <i class="calibre12">Waverley</i> or <i class="calibre12">Redgauntlet</i> and any scene of the
<i class="calibre12">Master of Ballantrae</i> or <i class="calibre12">Catriona</i>, whether in their
strength or weakness: and it is the most loyal lovers of the
older master who take the greatest pleasure in reading the work
of the younger, so much less opulently gifted as is
probable—though we must remember that Stevenson died at the
age when Scott wrote <i class="calibre12">Waverley</i>—so infinitely more
careful of his gift.  Stevenson may even blow upon the pipe
of Burns, and yet his tune will be no echo, but one which utters
the heart and mind of a Scots poet who has his own outlook on
life, his own special and profitable vein of smiling or satirical
contemplation.</p>
<p class="calibre7"><a id="pagexxiii"></a><span id="pgepubid00259" title="p. xxiii"></span>Not by reason, then, of ‘externality,’
for sure, nor yet of imitativeness, will this writer lose his
hold on the attention and regard of his countrymen.  The
debate, before his place in literature is settled, must rather
turn on other points: as whether the genial essayist and egoist
or the romantic inventor and narrator was the stronger in
him—whether the Montaigne and Pepys elements prevailed in
his literary composition or the Scott and Dumas elements—a
question indeed which among those who care for him most has
always been at issue.  Or again, what degree of true
inspiring and illuminating power belongs to the gospel, or
gospels, airily encouraging or gravely didactic, which are set
forth in the essays with so captivating a grace?  Or whether
in romance and tale he had a power of happily inventing and
soundly constructing a whole fable comparable to his
unquestionable power of conceiving and presenting single scenes
and situations in a manner which stamps them indelibly on the
reader’s mind.  And whether his figures are sustained
continuously by the true, large, spontaneous breath of creation,
or are but transitorily animated at critical and happy moments by
flashes of spiritual and dramatic insight, aided by the conscious
devices of his singularly adroit and spirited art?  This is
a question which no criticism but that of time can solve; it
takes the consenting instinct of generations to feel whether the
creatures of fiction, however powerfully they may strike at
first, are durably and equably, or ephemerally and fitfully,
alive.  To contend, as some do, that strong creative
impulse, and so keen an artistic <a id="pagexxiv"></a><span id="pgepubid00260" title="p. xxiv"></span>self-consciousness as
Stevenson’s was, cannot exist together, is quite
idle.  The truth, of course, is that the deep-seated
energies of imaginative creation are found sometimes in
combination, and sometimes not in combination, with an artistic
intelligence thus keenly conscious of its own purpose and
watchful of its own working.</p>
<p class="calibre7">Once more, it may be questioned whether, among the many
varieties of work which Stevenson has left, all touched with
genius, all charming and stimulating to the literary sense, all
distinguished by a grace and precision of workmanship which are
the rarest qualities in English art, there are any which can be
pointed to as absolute masterpieces, such as the future cannot be
expected to let die.  Let the future decide.  What is
certain is that posterity must either be very well, or very ill,
occupied if it can consent to give up so much sound
entertainment, and better than entertainment, as this writer
afforded his contemporaries.  In the meantime, among
judicious readers on both sides of the Atlantic, Stevenson
stands, I think it may safely be said, as a true master of
English prose; unsurpassed for the union of lenity and lucidity
with suggestive pregnancy and poetic animation; for harmony of
cadence and the well-knit structure of sentences; and for the art
of imparting to words the vital quality of things, and making
them convey the precise—sometimes, let it be granted, the
too curiously precise—expression of the very shade and
colour of the thought, feeling, or vision in his mind.  He
stands, moreover, as the writer who, in the last quarter of the
nineteenth <a id="pagexxv"></a><span id="pgepubid00261" title="p. xxv"></span>century, has handled with the most of freshness and
inspiriting power the widest range of established literary
forms—the moral, critical, and personal essay, travels
sentimental and other, romances and short tales both historical
and modern, parables and tales of mystery, boys’ stories of
adventure, memoirs—nor let lyrical and meditative verse
both English and Scottish, and especially nursery verse, a new
vein for genius to work in, be forgotten.  To some of these
forms Stevenson gave quite new life; through all alike he
expressed vividly an extremely personal way of seeing and being,
a sense of nature and romance, of the aspects of human existence
and problems of human conduct, which was essentially his
own.  And in so doing he contrived to make friends and even
lovers of his readers.  Those whom he attracts at all (and
there is no writer who attracts every one) are drawn to him over
and over again, finding familiarity not lessen but increase the
charm of his work, and desiring ever closer intimacy with the
spirit and personality which they divine behind it.</p>
<p class="calibre7">As to the fitting scale, then, on which to treat the memory of
a man who fills five years after his death such a place as this
in the public regard, the words ‘selection’ and
‘sketch’ have evidently to be given a pretty liberal
interpretation.  Readers, it must be supposed, will scarce
be content without both a fairly full biography, and the
opportunity of a fairly ample intercourse with the man as he was
accustomed to reveal himself in writing to his familiars. 
As to form—Stevenson’s own words and the nature of
the material alike seem to <a id="pagexxvi"></a><span id="pgepubid00262" title="p. xxvi"></span>indicate that the <i class="calibre12">Life</i> and
the <i class="calibre12">Letters</i> should be kept separate.  There are some
kinds of correspondence which can conveniently be woven into the
body and texture of a biography, though indeed I think it is a
plan to which biographers are much too partial.  Nothing,
surely, more checks the flow of a narrative than its interruption
by stationary blocks of correspondence; nothing more disconcerts
the reader than a too frequent or too abrupt alternation of
voices between the subject of a biography speaking in his letters
and the writer of it speaking in his narrative.  At least it
is only when letters are occupied, as Macaulay’s for
instance were, almost entirely with facts and events, that they
can without difficulty be handled in this way.  But events
and facts, ‘sordid facts,’ as he called them, were
not very often suffered to intrude into Stevenson’s
correspondence.  ‘I deny,’ he writes,
‘that letters should contain news (I mean mine; those of
other people should).  But mine should contain appropriate
sentiments and humorous nonsense, or nonsense without the
humour.’  Business letters, letters of information,
and letters of courtesy he had sometimes to write: but when he
wrote best was under the influence of the affection or
impression, or the mere whim or mood, of the moment; pouring
himself out in all manner of rhapsodical confessions and
speculations, grave or gay, notes of observation and criticism,
snatches of remembrance and autobiography, moralisings on matters
uppermost for the hour in his mind, comments on his own work or
other people’s, or mere idle fun and foolery.</p>
<p class="calibre7"><a id="pagexxvii"></a><span id="pgepubid00263" title="p. xxvii"></span>With a letter-writer of this character, as it seems
to me, a judicious reader desires to be left as much alone as
possible.  What he wants is to relish the correspondence by
itself, or with only just so much in the way of notes and
introductions as may serve to make allusions and situations
clear.  Two volumes, then, of letters so edited, to be
preceded by a separate introductory volume of narrative and
critical memoir, or <i class="calibre12">étude</i>—such was to be the
memorial to my friend which I had planned, and hoped by this time
to have ready.  Unfortunately, the needful leisure has
hitherto failed me, and might fail me for some time yet, to
complete the separate volume of biography.  That is now, at
the wish of the family, to be undertaken by Stevenson’s
cousin and my friend, Mr. Graham Balfour.  Meanwhile the
<i class="calibre12">Letters</i>, with introductions and notes somewhat extended
from the original plan, are herewith presented as a substantive
work by themselves.</p>
<p class="calibre7">The book will enable those who know and love their Stevenson
already to know him more intimately, and, as I hope, to love him
more.  It contains, certainly, much that is most essentially
characteristic of the man.  To some, perhaps, that very lack
of art as a correspondent of which we have found him above
accusing himself may give the reading an added charm and
flavour.  What he could do as an artist we know—what a
telling power and heightened thrill he could give to all his
effects, in so many different modes of expression and
composition, by calculated skill and the deliberate exercise of a
perfectly trained faculty.  This is the quality which nobody
<a id="pagexxviii"></a><span id="pgepubid00264" title="p. xxviii"></span>denies him, and which so deeply impressed his
fellow-craftsmen of all kinds.  I remember the late Sir John
Millais, a shrewd and very independent judge of books, calling
across to me at a dinner-table, ‘You know Stevenson,
don’t you?’ and then going on, ‘Well, I wish
you would tell him from me, if he cares to know, that to my mind
he is the very first of living artists.  I don’t mean
writers merely, but painters and all of us: nobody living can see
with such an eye as that fellow, and nobody is such a master of
his tools.’  Now in his letters, excepting a few
written in youth, and having more or less the character of
exercises, and a few in after years which were intended for the
public eye, Stevenson the deliberate artist is scarcely
forthcoming at all.  He does not care a fig for order or
logical sequence or congruity, or for striking a key of
expression and keeping it, but becomes simply the most
spontaneous and unstudied of human beings.  He will write
with the most distinguished elegance on one day, with simple good
sense and good feeling on a second, with flat triviality on
another, and with the most slashing, often ultra-colloquial,
vehemence on a fourth, or will vary through all these moods and
more in one and the same letter.  He has at his command the
whole vocabularies of the English and Scottish languages,
classical and slang, with good stores of the French, and tosses
and tumbles them about irresponsibly to convey the impression or
affection, the mood or freak of the moment.  Passages or
phrases of the craziest schoolboy or seafaring slang come
tumbling after and capping others of classical <a id="pagexxix"></a><span id="pgepubid00265" title="p. xxix"></span>cadence
and purity, of poetical and heartfelt eloquence.  By<span class="smcap"> </span>this medley of moods and manners,
Stevenson’s letters at their best—the pick, let us
say, of those in the following volumes which were written from
Hyères or Bournemouth—come nearer than anything else
to the full-blooded charm and variety of his conversation.</p>
<p class="calibre7">Nearer, yet not quite near; for it was in company only that
this genial spirit rose to his very best.  Those whom his
writings charm or impress, but who never knew him, can but
imagine how doubly they would have been charmed and impressed by
his presence.  Few men probably, certainly none that I have
ever seen or read of, have had about them such a richness and
variety of human nature; and few can ever have been better gifted
than he was to express the play of being that was in him by means
of the apt, expressive word and the animated look and
gesture.  <i class="calibre12">Divers et ondoyant</i>, in the words of
Montaigne, beyond other men, he seemed to contain within himself
a whole troop of singularly assorted characters—the poet
and artist, the moralist and preacher, the humourist and jester,
the man of great heart and tender conscience, the man of eager
appetite and curiosity, the Bohemian, impatient of restraints and
shams, the adventurer and lover of travel and of action:
characters, several of them, not rare separately, especially
among his Scottish fellow-countrymen, but rare indeed to be found
united, and each in such fulness and intensity, within the bounds
of a single personality.</p>
<p class="calibre7">Before all things Stevenson was a born poet, to whom the world
was full of enchantment and of latent romance, <a id="pagexxx"></a><span id="pgepubid00266" title="p. xxx"></span>only
waiting to take shape and substance in the forms art.  It
was his birthright—</p>
<blockquote class="calibre21"><div class="container"><p class="calibre7">            ‘to
hear<br class="calibre5"/>
The great bell beating far and near—<br class="calibre5"/>
The odd, unknown, enchanted gong<br class="calibre5"/>
That on the road hales men along,<br class="calibre5"/>
That from the mountain calls afar,<br class="calibre5"/>
That lures the vessel from a star,<br class="calibre5"/>
And with a still, aerial sound<br class="calibre5"/>
Makes all the earth enchanted ground.’</p>
</div>
</blockquote>
<p class="calibre7">At the same time, he was not less a born preacher and moralist
after his fashion.  A true son of the Covenanters, he had
about him little spirit of social or other conformity; but an
active and searching private conscience kept him for ever calling
in question both the grounds of his own conduct and the validity
of the accepted codes and compromises of society.  He must
try to work out a scheme of morality suitable to his own case and
temperament, which found the prohibitory law of Moses chill and
uninspiring, but in the Sermon on the Mount a strong incentive to
all those impulses of pity and charity to which his heart was
prone.  In youth his sense of social injustice and the
inequalities of human opportunity made him inwardly much of a
rebel, who would have embraced and acted on theories of socialism
or communism, could he have found any that did not seem to him at
variance with ineradicable instincts of human nature. <a id="citationxxx"></a><span class="citation" href="#/Text/Section0011#footnotexxx" title="Link target outside of Sample">[xxx]</span>  <a id="pagexxxi"></a><span id="pgepubid00267" title="p. xxxi"></span>All his life the artist and the
moralist in him alike were in rebellion against the bourgeois
spirit,—against timid, negative, and shuffling substitutes
for active and courageous well-doing,—and declined to
worship at the shrine of what he called the bestial goddesses
Comfort and Respectability.  The moralist in him helped the
artist by backing with the force of a highly sensitive conscience
his instinctive love of perfection in his work.  The poet
and artist qualified the moralist by discountenancing any
preference for the harsh, the sour, or the self-mortifying forms
of virtue, and encouraging the love for all tender or heroic,
glowing, generous and cheerful forms.</p>
<p class="calibre7">In another aspect of his many-sided being Stevenson was not
less a born adventurer and practical experimentalist in
life.  Many poets are content to dream, and many, perhaps
most, moralists to preach; but Stevenson must ever be doing and
undergoing.  He was no sentimentalist, to pay himself with
fine feelings whether for mean action or slack inaction.  He
had an insatiable zest for all experiences, not the pleasurable
only, but including even the more harsh and biting—those
that bring home to a man the pinch and sting of existence as it
is realised by the disinherited of the world, and excluding only
what he thought the prim, the conventional, the dead-alive, and
the cut-and-dry.  On occasion the experimentalist and man of
adventure in him would enter into special partnership with the
moralist and man of conscience; he loved to find himself in
difficult social passes and ethical dilemmas for the sake of
trying to behave in them to the utmost <a id="pagexxxii"></a><span id="pgepubid00268" title="p. xxxii"></span>according to his own personal sense of the
obligations of honour, duty, and kindness.  In yet another
part of his being, he cherished, as his great countryman Scott
had done before him, an intense underlying longing for the life
of action, danger, and command.  ‘Action, Colvin,
action,’ I remember his crying eagerly to me with his hand
on my arm as we lay basking for his health’s sake in a boat
off the scented shores of the Cap St. Martin.  Another
time—this was on his way to a winter cure at
Davos—some friend had given him General Hamley’s
<i class="calibre12">Operations of War</i>:—‘in which,’ he writes
to his father, ‘I am drowned a thousand fathoms deep, and O
that I had been a soldier is still my cry.’  In so
frail a tabernacle was it that the aspirations of the artist, the
unconventional moralist, the lover of all experience, and the
lover of daring action had to learn to reconcile themselves as
best they might.  Frail as it was, it contained withal a
strong animal nature, and he was as much exposed to the storms
and solicitations of sense as to the cravings and questionings of
the spirit.  Fortunately, with all these ardent and divers
instincts, there were present two invaluable gifts
besides—that of humour, which for all his stress of being
and vivid consciousness of self saved him from ever seeing
himself for long together out of a just proportion, and kept
wholesome laughter always ready at his lips; and that of a
perfectly warm, loyal, and tender heart, which through all his
experiments and agitations made the law of kindness the one
ruling law of his life.  In the end, lack of health
determined his <a id="pagexxxiii"></a><span id="pgepubid00269" title="p. xxxiii"></span>career, giving the chief part in his life to the
artist and man of imagination, and keeping the man of action a
prisoner in the sickroom until, by a singular turn of destiny, he
was able to wring a real, prolonged, and romantically successful
adventure out of that voyage to the Pacific which had been, in
its origin, the last despairing resource of the invalid.</p>
<p class="calibre7">To take this multiple personality from another point of view,
it was part of his genius that he never seemed to be cramped like
the rest of us, at any given time of life, within the limits of
his proper age, but to be child, boy, young man, and old man all
at once.  There was never a time in his life when Stevenson
had to say with St. Augustine, ‘Behold! my childhood is
dead, but I am alive.’  The child, as his <i class="calibre12">Garden of
Verses</i> vividly attests, and as will be seen by abundant
evidence in the course of the following pages, lived on always in
him, not in memory only, but in real survival, with all its
freshness of perception unimpaired, and none of its play
instincts in the least degree extinguished or made ashamed. 
As for the perennial boy in Stevenson, that is too apparent to
need remark.  It was as a boy for boys that he wrote the
best known of his books, <i class="calibre12">Treasure Island</i>; with all boys
that he met, provided they were really boys and not prigs nor
puppies, he was instantly at home; and the ideal of a career
which he most inwardly and longingly cherished, the ideals of
practical adventure and romance, of desirable predicaments and
gratifying modes of escape from them, were from first to last
those of a boy.  At the same time, even when I first <a id="pagexxxiv"></a><span id="pgepubid00270" title="p. xxxiv"></span>knew
him, there were about him occasional traits and glimpses of old
sagacity, of premature life-wisdom and experience, such as find
expression, for instance, in the essay <i class="calibre12">Virginibus
Puerisque</i>, among other matter more according with his then
age of twenty-six.</p>
<p class="calibre7">Again, it is said that in every poet there must be something
of the woman—the receptivity, the emotional nature. 
If to be impressionable in the extreme, quick in sympathy and
feeling, ardent in attachment, and full of pity for the weak and
suffering, is to be womanly, Stevenson was certainly all those;
he was even like a woman in being
<i class="calibre12">ἀρτίδακρυς</i>,
easily moved to tears at the touch of pity or affection, or even
at any specially poignant impression of art or beauty.  But
yet, if any one word were to be chosen for the predominant
quality of his character and example, I suppose that word would
be manly.  In all his habits and instincts he was the least
effeminate of men; and effeminacy, or aught approaching
sexlessness, was perhaps the only quality in man with which he
had no patience.  In his gentle and complying nature there
were strains of iron tenacity and will.  He had both kinds
of physical courage—the active, delighting in danger, and
the passive, unshaken in endurance.  In the moral courage of
facing situations and consequences, of cheerful self-discipline
and readiness to pay for faults committed, of outspokenness,
admitting no ambiguous relations and clearing away the clouds
from human intercourse, I have not known his equal.  His
great countryman Scott, as this book will prove, was not more
manfully free from artistic jealousy or the <a id="pagexxxv"></a><span id="pgepubid00271" title="p. xxxv"></span>least
shade of irritability under criticism, or more modestly and
unfeignedly inclined to exaggerate the qualities of other
people’s work and to underrate those of his own.  His
severest critic was always himself; the next most severe, those
of his own household and intimacy, whose love made them jealous
lest he should fall short of his best; for he lived in an
atmosphere of love, indeed, but not of flattery.  Of the
humorous and engaging parts of vanity and egoism, which led him
to make infinite talk and fun about himself, and use his own
experiences as a key for unlocking the confidences of others,
Stevenson had plenty; but of the morose and fretful parts never a
shade.  ‘A little Irish girl,’ he wrote once
during a painful crisis of his life, ‘is now reading my
book aloud to her sister at my elbow; they chuckle, and I feel
flattered.—Yours, R. L. S.  <i class="calibre12">P.S.</i> Now they yawn,
and I am indifferent.  Such a wisely conceived thing is
vanity.’  If only vanity so conceived were
commoner!  And whatever might be the abstract and
philosophical value of that somewhat grimly stoical conception of
the universe, of conduct and duty, at which in mature years he
had arrived, want of manliness is certainly not its fault. 
Nor is any such want to be found in the practice which he founded
on or combined with it; in his invincible gaiety and sweetness
under sufferings and deprivations the most galling to him; in the
temper which made his presence in health or sickness a perpetual
sunshine to those about him.  Take the kind of maxims of
life which he was accustomed to forge for himself and to act
by:—‘Acts may be forgiven; not <a id="pagexxxvi"></a><span id="pgepubid00272" title="p. xxxvi"></span>even
God can forgive the hanger-back.’  ‘Choose the
best, if you can; or choose the worst; that which hangs in the
wind dangles from a gibbet.’  ‘“Shall
I?” said Feeble-mind; and the echo said,
“Fie!”’  ‘“Do I love?”
said Loveless; and the echo laughed.’  ‘A fault
known is a fault cured to the strong; but to the weak it is a
fetter riveted.’  ‘The mean man doubts, the
great-hearted is deceived.’  ‘Great-heart was
deceived.  “Very well,” said
Great-heart.’  ‘“I have not forgotten my
umbrella,” said the careful man; but the lightning struck
him.’  ‘Nullity wanted nothing; so he supposed
he wanted advice.’  ‘Evil was called Youth till
he was old, and then he was called Habit.’ 
‘Fear kept the house; and still he must pay
taxes.’  ‘Shame had a fine bed, but where was
slumber?  Once he was in jail he slept.’  With
this moralist maxims meant actions; and where shall we easily
find a much manlier spirit of wisdom than this?</p>
<p class="calibre7">There was yet another and very different side to Stevenson
which struck others more than it struck myself, namely, that of
the perfectly freakish, not perfectly human, irresponsible madcap
or jester which sometimes appeared in him.  It is true that
his demoniac quickness of wit and intelligence suggested
occasionally a ‘spirit of air and fire’ rather than
one of earth; that he was abundantly given to all kinds of quirk
and laughter; and that there was no jest (saving the unkind) he
would not make and relish.  In the streets of Edinburgh he
had certainly been known for queer pranks and mystifications in
youth; and up to middle life there seemed <a id="pagexxxvii"></a><span id="pgepubid00273" title="p. xxxvii"></span>to
some of his friends to be much, if not of the Puck, at least of
the Ariel, about him.  The late Mr. J. A. Symonds always
called him Sprite; qualifying the name, however, by the epithets
‘most fantastic, but most human.’  To me the
essential humanity was always the thing most apparent.  In a
fire well nourished of seasoned ship-timber, the flames glance
fantastically and of many colours, but the glow at heart is ever
deep and strong; it was at such a glow that the friends of
Stevenson were accustomed to warm their hands, while they admired
and were entertained by the shifting lights.</p>
<p class="calibre7">It was only in talk, as I have said, that all the many lights
and colours of this richly compounded spirit could be seen in
full play.  He would begin no matter how—in early days
often with a jest at his own absurd garments, or with the
recitation, in his vibrating voice and full Scotch accent, of
some snatch of poetry that was haunting him, or with a rhapsody
of analytic delight over some minute accident of beauty or
expressiveness that had struck his observation, and would have
escaped that of everybody else, in man, woman, child, or external
nature.  And forthwith the floodgates would be opened, and
the talk would stream on in endless, never importunate, flood and
variety.  A hundred fictitious characters would be invented,
differentiated, and launched on their imaginary careers; a
hundred ingenious problems of conduct and cases of honour would
be set and solved, in a manner often quite opposed to
conventional precept; romantic voyages would be planned and
followed out in vision, with a thousand incidents, to all the
corners <a id="pagexxxviii"></a><span id="pgepubid00274" title="p. xxxviii"></span>of our own planet and of others; the possibilities
of life and art would be illuminated with glancing search-lights
of bewildering range and penetration, the most sober argument
alternating with the maddest freaks of fancy, high poetic
eloquence with coruscations of insanely apposite slang—the
earthiest jape anon shooting up into the empyrean and changing
into the most ethereal fantasy—the stalest and most
vulgarised forms of speech gaining brilliancy and illuminating
power from some hitherto undreamt-of application—and all
the while an atmosphere of goodwill diffusing itself from the
speaker, a glow of eager benignity and affectionate laughter
emanating from his presence, till every one about him seemed to
catch something of his own gift and inspiration.  This
sympathetic power of inspiring others was the special and
distinguishing note of Stevenson’s conversation.  He
would keep a houseful or a single companion entertained all day,
and day after day and half the nights, yet never seemed to
dominate the talk or absorb it; rather he helped every one about
him to discover and to exercise unexpected powers of their
own.  The point could hardly be better brought out than it
is in a fragment which I borrow from Mr. Henley of an unpublished
character-sketch of his friend: ‘I leave his praise in this
direction (the telling of Scottish vernacular stories) to
others.  It is more to my purpose to note that he will
discourse with you of morals, music, marbles, men, manners,
metaphysics, medicine, mangold-wurzel—<i class="calibre12">que
scays-je</i>?—with equal insight into essentials and equal
pregnancy and felicity of utterance; and that he will <a id="pagexxxix"></a><span id="pgepubid00275" title="p. xxxix"></span>stop
with you to make mud pies in the first gutter, range in your
company whatever heights of thought and feeling you have found
accessible, and end by guiding you to altitudes far nearer the
stars than you have ever dreamed of footing it; and that at the
last he makes you wonder which to admire the more—his easy
familiarity with the Eternal Veracities or the brilliant flashes
of imbecility with which his excursions into the Infinite are
sometimes diversified.  He radiates talk, as the sun does
light and heat; and after an evening—or a week—with
him, you come forth with a sense of satisfaction in your own
capacity which somehow proves superior even to the inevitable
conclusion that your brilliance was but the reflection of his
own, and that all the while you were only playing the part of
Rubinstein’s piano or Sarasate’s violin.’</p>
<p class="calibre7">All this the reader should imagine as helped by the most
speaking of presences: a steady, penetrating fire in the wide-set
eyes, a compelling power and sweetness in the smile; courteous,
waving gestures of the arms and long, nervous hands, a lit
cigarette generally held between the fingers; continual rapid
shiftings and pacings to and fro as he conversed: rapid, but not
flurried nor awkward, for there was a grace in his attenuated but
well-carried figure, and his movements were light, deft, and full
of spring.  When I first knew him he was passing through a
period of neatness between two of Bohemian carelessness as to
dress; so that the effect of his charm was immediate.  At
other times of his youth there was something for strangers, and
even for friends, to get over in <a id="pagexl"></a><span id="pgepubid00276" title="p. xl"></span>the odd garments which it was his
whim to wear—the badge, as they always seemed to me, partly
of a genuine carelessness, certainly of a genuine lack of cash
(the little he had was always absolutely at the disposal of his
friends), partly of a deliberate detachment from any particular
social class or caste, partly of his love of pickles and
adventures, which he thought befel a man thus attired more
readily than another.  But this slender, slovenly,
nondescript apparition, long-visaged and long-haired, had only to
speak in order to be recognised in the first minute for a witty
and charming gentleman, and within the first five for a master
spirit and man of genius.  There were, indeed, certain
stolidly conventional and superciliously official kinds of
persons, both at home and abroad, who were incapable of looking
beyond the clothes, and eyed him always with frozen
suspicion.  This attitude used sometimes in youth to drive
him into fits of flaming anger, which put him helplessly at a
disadvantage unless, or until, he could call the sense of humour
to his help.  For the rest, his human charm was the same for
all kinds of people, without the least distinction of class or
caste; for worldly wise old great ladies, whom he reminded of
famous poets in their youth; for his brother artists and men of
letters, perhaps, above all; for the ordinary clubman; for his
physicians, who could never do enough for him; for domestic
servants, who adored him; for the English policeman even, on whom
he often tried, quite in vain, to pass himself as one of the
criminal classes; for the common seaman, the shepherd, the street
arab, or the tramp.  Even in the imposed silence <a id="pagexli"></a><span id="pgepubid00277" title="p. xli"></span>and
restraint of extreme sickness the magnetic power and attraction
of the man made itself felt, and there seemed to be more vitality
and fire of the spirit in him as he lay exhausted and speechless
in bed than in an ordinary roomful of people in health.</p>
<p class="calibre7">But I have strayed from my purpose, which is only to indicate
that in the best of these letters of Stevenson’s you have
some echo, far away indeed, but yet the nearest, of his
talk—talk which could never be taken down, and has left
only an ineffaceable impression in the memory of his
friends.  The letters, it should be added, do not represent
him at all fully until about the thirtieth year of his age, the
beginning of the settled and married period of his life. 
From then onwards, and especially from the beginning of Part
<span class="gutsmall1">VI</span>. (the Hyères period),
they present a pretty full and complete autobiography, if not of
doings, at any rate of moods and feelings.  In the earlier
periods, his correspondence for the most part expresses his real
self either too little or else one-sidedly.  I have omitted
very many letters of his boyish and student days as being too
immature or uninteresting; and many of the confidences and
confessions of his later youth, though they are those of a
beautiful spirit, whether as too intimate, or as giving a
disproportionate prominence to passing troubles.  When he is
found in these days writing in a melancholy or minor key, it must
be remembered that at the same moment, in direct intercourse with
any friend, his spirits would instantly rise, and he would be
found the gayest of laughing companions.  Very many letters
or snatches <a id="pagexlii"></a><span id="pgepubid00278" title="p. xlii"></span>of letters of nearly all dates to his familiars have
also been omitted as not intelligible without a knowledge of the
current jests, codes, and catchwords of conversation between him
and them.  At one very interesting period of his life, from
about his twenty-fifth to his twenty-ninth year, he disused the
habit of letter-writing almost entirely.</p>
<p class="calibre7">In choosing from among what remained I have used the best
discretion that I could.  Stevenson’s feelings and
relations throughout life were in almost all directions so warm
and kindly, that next to nothing had to be suppressed from fear
of giving pain.  On the other hand, he drew people towards
him with so much confidence and affection, and met their openness
with so much of his own, that an editor could not but feel the
frequent risk of inviting readers to trespass too far on purely
private affairs and feelings, including those of the
living.  This was a point upon which in his lifetime he felt
strongly.  That excellent critic, Mr. Walter Raleigh, has
noticed, as one of the merits of Stevenson’s personal
essays and accounts of travel, that few men have written more or
more attractively of themselves without ever taking the public
unduly into familiarity or overstepping proper bounds of
reticence.  Public prying into private lives, the
propagation of gossip by the press, and printing of private
letters during the writer’s lifetime, were things he
hated.  Once, indeed, he very superfluously gave himself a
dangerous cold by dancing before a bonfire in his garden at the
news of a ‘society’ editor having been committed to
prison; and the only approach to a difference he ever had with
one of his lifelong friends <a id="pagexliii"></a><span id="pgepubid00279" title="p. xliii"></span>arose from the publication,
without permission, of one of his letters written on his first
Pacific voyage (see below, vol. ii. p. 121).</p>
<p class="calibre7">How far, then, must I regard his instructions about
publication as authorising me to go after his death beyond the
limits which he had been so careful in observing and desiring
others to observe in life?  How much may now fairly become
public of that which had been held sacred and hitherto private
among his friends?  To cut out all that is strictly personal
and intimate were to leave his story untold and half the charm of
his character unrevealed; to put in too much were to break all
bonds of that privacy which he so carefully regarded while he
lived.  I know not if I have at all been able to hit the
mean, and to succeed in making these letters, as it has been my
object to make them, present, without offence or intrusion, a
just, a living, and a proportionate picture of the man, so far as
they will yield it.  There is one respect in which his own
practice and principle has had to be in some degree violated, if
the work was to be done at all.  Except in the single case
of the essay ‘Ordered South,’ he would never in
writing for the public adopt the invalid point of view, or invite
any attention to his infirmities.  ‘To me,’ he
says, ‘the medicine bottles on my chimney and the blood on
my handkerchief are accidents; they do not colour my view of
life; and I should think myself a trifler and in bad taste if I
introduced the world to these unimportant privacies.’ 
But from his letters to his family and friends, these matters
could not possibly be quite left out.  <a id="pagexliv"></a><span id="pgepubid00280" title="p. xliv"></span>The tale
of his life, in the years when he was most of a correspondent,
was in truth a tale of daily and nightly battle against weakness
and physical distress and danger.  To those who loved him,
the incidents of this battle were communicated, sometimes
gravely, sometimes laughingly.  I have very greatly cut down
such bulletins, but could not manage to omit them
altogether.  Generally speaking, I have used the editorial
privilege of omission without scruple where I thought it
desirable.  And in regard to the text, I have not held
myself bound to reproduce all the author’s minor
eccentricities of spelling and the like.  As all his friends
are aware, to spell in a quite accurate and grown-up manner was a
thing which this master of English letters was never able to
learn; but to reproduce such trivial slips in print is, I think,
to distract the reader’s attention from the main
matter.  A normal orthography has therefore been adopted
throughout.</p>
<p class="calibre7">Lastly, I have to express my thanks to my friend Mr. George
Smith, proprietor of the <i class="calibre12">Dictionary of National Biography</i>,
for permission to reprint in this and in following sectional
introductions a few paragraphs from that work.</p>
<p class="calibre22">S. C.</p>
<p class="calibre7"><i class="calibre12">August</i> 1899.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0003.vue?597d834b